import React, { Fragment, useEffect, useState, useRef } from "react";
import { listService } from "client-app-core";
import moment from "moment-timezone";
import ListToolbar from "./ListToolbar";
import { Dialog, Table } from "../../../../CBComponents";
import { RowEdit, AttachmentDialog } from "../../../../SharedComponents";
import _ from "lodash";
import {
	Avatar,
	Card,
	CardHeader,
	CardContent,
	Checkbox,
	Collapse,
	CircularProgress,
	TextField,
	IconButton
} from "@mui/material";
import { withStyles } from "@mui/styles";
import { ExpandMore, ExpandLess, DragIndicator } from "@mui/icons-material";
import { ViewList, Check } from "mdi-material-ui";
import { Translate, getTranslation } from "orion-components/i18n";
import { useDispatch, useSelector } from "react-redux";
import { userIntegrationByFeedIdSelector } from "orion-components/Session/Selectors";

const alertListActivityIntents = Object.freeze({
	editList: "edit-list",
	editRow: "edit-row",
	removeRows: "remove-rows",
	renameList: "rename-list",
	toggleRowCheckbox: "toggle-row-checkbox"
});

const styles = {
	cardContentRoot: {
		border: "1px solid #41454A",
		padding: "0!important"
	},
	headerAction: {
		margin: 0
	},
	headerAvatar: {
		margin: "0 16px 0 0"
	},
	headerAvatarRTL: {
		margin: "0 0 0 16px"
	},
	checkboxRoot: {
		color: "#828283",
		"&$checked": {
			color: "#35b7f3!important"
		}
	},
	paddingCheckbox: {
		padding: "0 0 0 4px"
	},
	checkboxChecked: {
		color: "#35b7f3"
	},
	inputRoot: {
		height: "100%"
	}
};

const ListCard = ({
	list,
	getLookupValues,
	lookupData,
	dialog,
	openDialog,
	closeDialog,
	dir,
	cardStyles,
	adding,
	checked,
	handleSelectList,
	canEdit,
	canRemove,
	user,
	secondaryExpanded,
	locale,
	defaultListPagination,
	classes,
	listPaginationOptions,
	collapsed = true,
	handleCollapseAndExpand,
	id,
	canModifyListRows,
	targetType,
	sortable
}) => {
	const dispatch = useDispatch();

	const userFeed = useSelector(state => userIntegrationByFeedIdSelector(list.feedId)(state));
	const canManage = userFeed?.permissions.includes("manage");
	const timeFormatPreference = useSelector(state => state.appState.global.timeFormat);
	const sourceAppId = useSelector(state => state?.application?.appId);

	const [editableRow, setEditableRow] = useState(null);
	const [removing, setRemoving] = useState(false);
	const [removals, setRemovals] = useState([]);
	const [showLoading, setShowLoading] = useState(false);
	const [title, setTitle] = useState(list.name);
	const [editTitle, setEditTitle] = useState(false);
	const [selectedRow, setSelectedRow] = useState(false);
	const [expandedState, setExpandedState] = useState(false);
	const [optimisticRows, setOptimisticRows] = useState(list.rows || []);

	useEffect(() => {
		handleLookupDataRetrieval();
	}, []);

	useEffect(() => {
		setOptimisticRows(list.rows || []);
	}, [list.rows]);

	useEffect(() => {
		setExpandedState(!collapsed);
	}, [collapsed]);

	const usePrevious = value => {
		const ref = useRef();
		useEffect(() => {
			ref.current = value;
		}, [value]);
		return ref.current;
	};

	const prevList = usePrevious(list);
	const updateTargetList = (update, intent, callback) => {
		listService.updatePinnedList(
			list.targetId,
			targetType,
			list.id,
			update,
			{ appId: sourceAppId, intent },
			callback
		);
	};

	useEffect(() => {
		if (prevList && prevList.columns.length < list.columns.length) {
			handleLookupDataRetrieval();
		}
	}, [list]);

	const deleteList = () => {
		setShowLoading(true);
		if (!list.targetId) {
			listService.deleteList(list.id, (err, res) => {
				if (err) {
					console.log(err, res);
				}
			});
		} else {
			listService.deletePinnedList(list.targetId, targetType, list.id, (err, res) => {
				if (err) {
					console.log(err, res);
				}
			});
		}
		//When the closeDialog or openDialog actions are triggered from toggleDialog, the redux action's meta throws an error, causing the remaining lines of code to stop.
		toggleDialog("deleteList" + list.id);
	};

	const deleteMode = () => {
		setRemoving(true);
	};

	const cancelDeleteMode = () => {
		setRemoving(false);
		setRemovals([]);
	};

	const handleLookupDataRetrieval = () => {
		const lookingUp = {};
		for (let i = 0; i < list.columns.length; i++) {
			if (
				list.columns[i].type === "lookup" &&
				!lookupData[list.columns[i].lookupType] &&
				!lookingUp[list.columns[i].lookupType]
			) {
				lookingUp[list.columns[i].lookupType] = true;
				dispatch(getLookupValues(list.columns[i].lookupType));
			}
		}
	};

	const handleKeyDown = event => {
		if (event) {
			if (event.key === "Enter" && editTitle) {
				handleSaveNewTitle();
			}
		}
	};

	const toggleEditTitle = () => {
		editTitle
			? document.removeEventListener("keydown", handleKeyDown())
			: document.addEventListener("keydown", handleKeyDown());
		setEditTitle(!editTitle);
	};

	const handleRenameList = event => {
		const title = event.target.value;
		setTitle(title);
	};

	const handleCancelRename = () => {
		setTitle(list.name);
		setEditTitle(false);
		toggleEditTitle();
	};

	const handleSaveNewTitle = () => {
		if (!list.targetId) {
			listService.updateList(list.id, { name: title }, (err, res) => {
				if (err) console.log(err, res);
			});
		} else {
			updateTargetList({ name: title }, alertListActivityIntents.renameList, (err, res) => {
				if (err) console.log(err, res);
			});
		}
		toggleEditTitle();
	};

	const handleCheckboxToggle = (order, id) => {
		const previousRows = optimisticRows;
		const newRows = _.cloneDeep(optimisticRows);
		const selectedRow = _.find(newRows, row => row.order === order);
		if (!selectedRow) return;

		const rowData = selectedRow.data;
		const toggled = selectedRow.data[id];
		if (`${id}_user-completed` in rowData) {
			if (toggled) {
				selectedRow.data[id + "_user-completed"] = "";
			} else {
				selectedRow.data[id + "_user-completed"] = user.name;
			}
		}
		if (`${id}_show-time` in rowData) {
			if (toggled) {
				selectedRow.data[id + "_show-time"] = "";
			} else {
				selectedRow.data[id + "_show-time"] = moment();
			}
		}
		selectedRow.data[id] = !toggled;
		setOptimisticRows(newRows);

		const handleUpdateError = (err, res) => {
			if (err) {
				console.log(err, res);
				setOptimisticRows(previousRows);
			}
		};
		if (!list.targetId) {
			listService.updateList(list.id, { rows: newRows }, handleUpdateError);
		} else {
			updateTargetList({ rows: newRows }, alertListActivityIntents.toggleRowCheckbox, handleUpdateError);
		}
	};

	const handleNotesChange = _.debounce((text, row, column) => {
		const newRows = _.cloneDeep(list.rows);
		_.find(newRows, newRow => newRow.order === row.order).data[column.id] = text;
		if (!list.targetId) {
			listService.updateList(list.id, { rows: newRows }, (err, res) => {
				if (err) console.log(err, res);
			});
		} else {
			updateTargetList({ rows: newRows }, alertListActivityIntents.editRow, (err, res) => {
				if (err) console.log(err, res);
			});
		}
	}, 1000);

	const handleToggleRemove = index => {
		const newRemovals = [...removals];
		if (_.includes(newRemovals, index)) {
			_.pull(newRemovals, index);
			setRemovals(newRemovals);
		} else setRemovals([...newRemovals, index]);
	};

	const toggleDialog = dialogRef => {
		dialog === dialogRef ? dispatch(closeDialog(dialogRef)) : dispatch(openDialog(dialogRef));
	};

	const handleConfirmRemove = listId => {
		const newRows = _.cloneDeep(list.rows);
		_.remove(newRows, row => _.includes(removals, row.order));
		if (!list.targetId) {
			listService.updateList(listId, { rows: newRows }, (err, res) => {
				if (err) console.log(err, res);
			});
		} else {
			updateTargetList({ rows: newRows }, alertListActivityIntents.removeRows, (err, res) => {
				if (err) console.log(err, res);
			});
		}
		setRemoving(false);
		setRemovals([]);
	};

	const handleRowEdit = (e, row, list) => {
		setSelectedRow(row);
		toggleDialog("rowEdit" + list.id);
	};

	const handleSetNowDateTime = (column, row, list) => {
		const newRows = _.cloneDeep(list.rows);
		_.find(newRows, newRow => newRow.order === row.order).data[column.id] = moment().toISOString();
		if (!list.targetId) {
			listService.updateList(list.id, { rows: newRows }, (err, res) => {
				if (err) console.log(err, res);
			});
		} else {
			updateTargetList({ rows: newRows }, alertListActivityIntents.editRow, (err, res) => {
				if (err) console.log(err, res);
			});
		}
	};

	const handleViewAttachments = (e, row, list) => {
		setSelectedRow(row);
		toggleDialog("attachmentDialog" + list.id);
	};

	const handleUpdateList = (listId, update) => {
		if (!list.targetId) {
			listService.updateList(listId, update, (err, res) => {
				if (err) console.log(err, res);
			});
		} else {
			updateTargetList(
				update,
				update.rows ? alertListActivityIntents.editRow : alertListActivityIntents.editList,
				(err, res) => {
					if (err) console.log(err, res);
				}
			);
		}
	};

	const handleExpandClick = () => {
		setExpandedState(!expandedState);
		handleCollapseAndExpand(id, !expandedState);
	};

	const getAvatar = () => {
		const avatarStyle = {
			backgroundColor: "#677588",
			border: "1px solid #FFFFFF",
			color: "#FFFFFF"
		};

		if (
			list.columns &&
			list.columns.length > 0 &&
			optimisticRows &&
			optimisticRows.length > 0 &&
			list.columns.sort(col => col.order)[0].type === "checkbox"
		) {
			const firstColumn = list.columns.sort(col => col.order)[0];
			if (firstColumn.type === "checkbox") {
				const numberIncomplete = optimisticRows.filter(row => {
					return row.data && row.data[firstColumn.id] === false;
				}).length;

				if (numberIncomplete > 0) {
					return <Avatar style={avatarStyle}>{numberIncomplete}</Avatar>;
				} else {
					avatarStyle.backgroundColor = "#61BE49";
					return (
						<Avatar style={avatarStyle}>
							<Check />
						</Avatar>
					);
				}
			}
		}

		return (
			<Avatar style={avatarStyle}>
				<ViewList />
			</Avatar>
		);
	};

	const Title = editTitle ? title : list.name;
	const subheader = list.categoryRef ? list.categoryRef.name : "";
	const listStyle = {
		spacer: {
			flex: "1 1 100%"
		},
		buttons: {
			flex: "0 0 auto"
		},
		rowDeleteFlex: {
			color: "white",
			display: "flex",
			alignItems: "center",
			maxWidth: "100%",
			minWidth: 70,
			justifyContent: "flex-start"
		}
	};

	// Setting header based on whether or not the list should be rendered as a list template to be added,
	// or as a list that is attached to an event
	let header;
	if (adding) {
		header = (
			<CardHeader
				style={{ ...cardStyles.header }}
				title={
					<p
						style={{
							fontSize: 14,
							color: "#FFFFFF",
							overflow: "hidden",
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							maxWidth: secondaryExpanded ? 420 : 205
						}}
					>
						{Title}
					</p>
				}
				subheader={
					<p
						style={{
							overflow: "hidden",
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							maxWidth: secondaryExpanded ? 420 : 205,
							color: "#B5B9BE",
							fontSize: 12
						}}
					>
						{subheader}
					</p>
				}
				action={
					<IconButton
						onClick={handleExpandClick}
						style={{ color: "#FFFFFF" }}
						data-testid="list-card-header-expand-button"
					>
						{expandedState ? <ExpandLess /> : <ExpandMore />}
					</IconButton>
				}
				classes={{ action: classes.headerAction }}
				avatar={
					<Checkbox
						color="primary"
						data-testid="list-card-select-checkbox"
						classes={{
							root: classes.checkboxRoot,
							checked: classes.checkboxChecked
						}}
						checked={checked}
						onChange={() => {
							handleSelectList(list.id);
						}}
					/>
				}
			/>
		);
	} else if (editTitle) {
		const textField = (
			<TextField
				id="title-field"
				data-testid="list-card-title-input"
				fullWidth={true}
				variant="standard"
				onChange={handleRenameList}
				value={Title}
				autoFocus
				style={{
					backgroundColor: "#41454a",
					height: 40,
					color: "#fff"
				}}
				InputProps={{
					classes: {
						root: classes.inputRoot
					}
				}}
			/>
		);
		header = (
			<CardHeader
				style={{
					...cardStyles.header,
					paddingTop: 0,
					paddingBottom: 0
				}}
				actAsExpander={false}
				showExpandableButton={true}
				title={textField}
			/>
		);
	} else {
		header = (
			<CardHeader
				style={{
					...cardStyles.header,
					backgroundColor: expandedState ? "#383D48" : "#41454A",
					direction: dir
				}}
				title={
					<p
						style={{
							fontSize: 14,
							color: "#FFFFFF",
							overflow: "hidden",
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							maxWidth: secondaryExpanded ? 420 : 205
						}}
					>
						{Title}
					</p>
				}
				subheader={
					<p
						style={{
							overflow: "hidden",
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							maxWidth: secondaryExpanded ? 420 : 205,
							color: "#B5B9BE",
							fontSize: 12
						}}
					>
						{subheader}
					</p>
				}
				action={
					<IconButton
						onClick={handleExpandClick}
						style={{ color: "#FFFFFF" }}
						data-testid="list-card-footer-expand-button"
					>
						{expandedState ? <ExpandLess /> : <ExpandMore />}
					</IconButton>
				}
				classes={{
					action: classes.headerAction,
					avatar: dir === "rtl" ? classes.headerAvatarRTL : classes.headerAvatar
				}}
				avatar={
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center"
						}}
					>
						{sortable && <DragIndicator sx={{ color: "#828283", cursor: "grab" }} />}
						{getAvatar()}
					</div>
				}
			/>
		);
	}

	const displayList = { ...list, rows: optimisticRows };
	const hasAttachments = _.size(_.filter(displayList.rows, row => _.size(row.attachments) > 0)) > 0;
	return list ? (
		<Card
			style={{ marginBottom: ".5rem", background: "transparent" }}
			key={`list-card-${list.id}`}
			data-testid="list-card"
		>
			{header}
			<Collapse
				in={expandedState}
				unmountOnExit
				style={{
					direction: dir
				}}
			>
				<CardContent classes={{ root: classes.cardContentRoot }} data-testid="list-card-content">
					{!adding && (
						<ListToolbar
							listId={list.id}
							expanded={expandedState}
							editableRow={editableRow}
							deleteMode={deleteMode}
							canRemove={canRemove}
							canEdit={canEdit}
							canManage={canManage}
							//cancelAddRow={cancelAddRow}
							cancelDeleteMode={cancelDeleteMode}
							//addRow={addRow}
							//submitAddRow={submitAddRow}
							startDeleteMode={deleteMode}
							submitDeleteMode={handleConfirmRemove}
							toggleDialog={toggleDialog}
							toggleEditTitle={toggleEditTitle}
							handleSaveNewTitle={handleSaveNewTitle}
							handleCancelRename={handleCancelRename}
							listStyle={listStyle}
							editTitle={editTitle}
							removing={removing}
							dir={dir}
						/>
					)}
					<Table
						columns={_.orderBy(list.columns, ["order"])}
						listPaginationOptions={listPaginationOptions}
						defaultListPagination={defaultListPagination}
						rows={displayList.rows}
						noPagination={list.noPagination}
						canViewNotes={canEdit}
						classes={{
							tableWrapper: "list-body-wrapper"
						}}
						removing={removing}
						removals={removals}
						handleChange={handleNotesChange}
						toggleRemove={handleToggleRemove}
						selectKey={canEdit ? "required" : null}
						selectAction={(e, row) => handleRowEdit(e, row, displayList)}
						setNowAction={(column, row) => handleSetNowDateTime(column, row, displayList)}
						handleCheckboxToggle={handleCheckboxToggle}
						hasAttachments={hasAttachments}
						attachmentAction={(e, row) => handleViewAttachments(e, row, displayList)}
						disableCheckbox={!canEdit}
						dir={dir}
						paginationSlotProps={{
							select: {
								MenuProps: {
									slotProps: {
										paper: {
											sx: {
												backgroundColor: "#2c2d2f"
											}
										}
									}
								}
							}
						}}
						timeFormatPreference={timeFormatPreference} data-testid="orion-components-profiles-widgets-list-components-list-card-table"
					/>
				</CardContent>
			</Collapse>
			{dialog && (
				<Fragment>
					<Dialog
						open={dialog === "deleteList" + list.id}
						confirm={{
							label: getTranslation("global.profiles.widgets.list.listCard.delete"),
							action: deleteList
						}}
						abort={{
							label: getTranslation("global.profiles.widgets.list.listCard.cancel"),
							action: () => toggleDialog("deleteList" + list.id)
						}}
						options={{
							onClose: () => toggleDialog("deleteList" + list.id),
							maxWidth: "md"
						}}
						dir={dir} data-testid="orion-components-profiles-widgets-list-components-list-card-dialog"
					>
						<p className="dialog-text" style={{ color: "#fff" }} data-testid="orion-components-profiles-widgets-list-components-list-card-p">
							<Translate value="global.profiles.widgets.list.listCard.confirmationText" />
						</p>
						{showLoading ? <CircularProgress size={60} thickness={5} data-testid="orion-components-profiles-widgets-list-components-list-card-circular-progress" /> : null}
					</Dialog>
					<RowEdit
						key={list.id + "-add-row"}
						list={list}
						dialogRef={"rowAdd" + list.id}
						adding={true}
						lookupData={lookupData}
						user={user}
						handleCloseDialog={toggleDialog}
						dialog={dialog}
						updateList={handleUpdateList}
						dir={dir}
						locale={locale}
						canModifyListRows={canModifyListRows}
					/>
					<RowEdit
						key={list.id + "-edit-row"}
						list={list}
						lookupData={lookupData}
						user={user}
						dialogRef={"rowEdit" + list.id}
						row={selectedRow}
						adding={false}
						handleCloseDialog={toggleDialog}
						dialog={dialog}
						updateList={handleUpdateList}
						dir={dir}
						locale={locale}
						canModifyListRows={canModifyListRows}
					/>
					<AttachmentDialog
						key={list.id + "-attachments"}
						list={list}
						dialogRef={"attachmentDialog" + list.id}
						row={selectedRow}
						handleCloseDialog={toggleDialog}
						dialog={dialog}
						dir={dir}
					/>
				</Fragment>
			)}
		</Card>
	) : (
		<div />
	);
};

export default withStyles(styles)(ListCard);
